import { useState, useMemo, useEffect } from 'react';
import { Shield, Lock, CheckCircle2, Mail, Video, FileText, CreditCard, Users, Phone, TrendingUp, TrendingDown, History, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import NavShell from '../components/NavShell';
import WritingSampleModal from '../components/WritingSampleModal';
import VideoVerificationModal from '../components/VideoVerificationModal';
import PhoneVerifyModal from '../components/PhoneVerifyModal';
import { useProfileAttributes, useMe } from '../hooks/useProfile';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { addEmail, createIdentitySession, fetchTrustHistory, type TrustHistoryEntry } from '../lib/api';
import { TRUST_LEVEL_NAMES, TRUST_RECOVERY_DELTAS, TRUST_VERIFICATION_DELTAS } from '@swipeopinion/shared';

// The advertised "+N trust score" per verification reads from the shared
// TRUST_VERIFICATION_DELTAS so the copy can never drift from what the API award
// code grants. ('social' has no API grant yet — Phase B placeholder.)
const VERIFICATION_DEFS = [
  { id: 'email', name: 'Email Verification', trustScore: TRUST_VERIFICATION_DELTAS.SIGNUP, rewardPoints: 0, description: 'Verify your email address', icon: Mail, alwaysComplete: false, comingSoon: false, attrKey: null },
  { id: 'phone', name: 'Phone Verification', trustScore: TRUST_VERIFICATION_DELTAS.PHONE_VERIFIED, rewardPoints: 0, description: 'Verify your phone number', icon: Phone, alwaysComplete: false, comingSoon: false, attrKey: null },
  { id: 'video', name: 'Video Verification', trustScore: TRUST_VERIFICATION_DELTAS.VIDEO_VERIFIED, rewardPoints: 0, description: 'Record a 30-second video to verify you\'re a real person', icon: Video, alwaysComplete: false, comingSoon: false, attrKey: 'video_verification_score' },
  { id: 'writing', name: 'Writing Sample', trustScore: TRUST_VERIFICATION_DELTAS.WRITING_SAMPLE, rewardPoints: 20, description: 'Provide a writing sample', icon: FileText, alwaysComplete: false, comingSoon: false, attrKey: 'writing_sample_score' },
  { id: 'id', name: 'ID Verification', trustScore: TRUST_VERIFICATION_DELTAS.IDENTITY_VERIFIED, rewardPoints: 0, description: 'Submit a government-issued ID', icon: CreditCard, alwaysComplete: false, comingSoon: true, attrKey: null },
  { id: 'social', name: 'Social Account', trustScore: 15, rewardPoints: 0, description: 'Link a social media account', icon: Users, alwaysComplete: false, comingSoon: true, attrKey: null },
];

// Level bands (inclusive):
//   L1 Basic    0–25
//   L2 Rising   26–40
//   L3 Trusted  41–60
//   L4 Elite    61–90
//   L5 Champion 91–100
// Names come from the shared TRUST_LEVEL_NAMES table (single source of truth).
const LEVEL_BENEFITS = [
  { level: 1, name: TRUST_LEVEL_NAMES[1], range: '0–25',  description: 'Access standard surveys and offers.', unlocked: true },
  { level: 2, name: TRUST_LEVEL_NAMES[2], range: '26–40', description: 'Redeem sooner — cash out starting at 500 points.', unlocked: false },
  { level: 3, name: TRUST_LEVEL_NAMES[3], range: '41–60', description: 'Premium surveys with higher payouts.', unlocked: false },
  { level: 4, name: TRUST_LEVEL_NAMES[4], range: '61–90', description: 'Your earned points are available instantly — no 30-day waiting period.', unlocked: false, moneyGated: true },
  { level: 5, name: TRUST_LEVEL_NAMES[5], range: '91–100', description: 'Redemptions clear without review — no waiting on approval.', unlocked: false, moneyGated: true },
];

function formatRelativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} minute${min === 1 ? '' : 's'} ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hour${hr === 1 ? '' : 's'} ago`;
  const days = Math.floor(hr / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  const months = Math.floor(days / 30);
  return `${months} month${months === 1 ? '' : 's'} ago`;
}

function formatShortDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function ScoreHistoryRow({ entry }: { entry: TrustHistoryEntry }) {
  // Amount hidden (e.g. support adjustment): neutral row, no +/− and no direction.
  const hidden = entry.hideAmount || entry.delta === null;
  const positive = !hidden && (entry.delta ?? 0) > 0;
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-[#F1F5F9] last:border-0">
      <div className="flex items-center gap-3 min-w-0">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${hidden ? 'bg-[#EEF2FF]' : positive ? 'bg-[#10B981]/10' : 'bg-red-50'}`}>
          {hidden
            ? <Shield className="w-4 h-4 text-[#5B4FE8]" />
            : positive
              ? <TrendingUp className="w-4 h-4 text-[#10B981]" />
              : <TrendingDown className="w-4 h-4 text-red-500" />}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-[#111827] truncate">{entry.label}</p>
          <p className="text-[11px] text-[#6B7280]">{formatRelativeTime(entry.createdAt)}</p>
        </div>
      </div>
      {!hidden && (
        <span className={`text-sm font-bold tabular-nums ${positive ? 'text-[#10B981]' : 'text-red-500'}`}>
          {positive ? '+' : ''}{entry.delta}
        </span>
      )}
    </div>
  );
}

export default function TrustCenterPage() {
  const [writingModalOpen, setWritingModalOpen] = useState(false);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [phoneModalOpen, setPhoneModalOpen] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [identityStarting, setIdentityStarting] = useState(false);
  const [identityError, setIdentityError] = useState(false);
  const [identityPollDone, setIdentityPollDone] = useState(false);
  const { data: attributes } = useProfileAttributes();
  const { data: me } = useMe();
  const queryClient = useQueryClient();
  const { data: trustHistory } = useQuery({ queryKey: ['trust', 'history'], queryFn: fetchTrustHistory });
  const [searchParams] = useSearchParams();

  const meRecord = me as Record<string, unknown> | undefined;
  const identityVerified = meRecord?.identity_verified === true;
  const identityVerifiedAt = meRecord?.identity_verified_at as string | null | undefined;
  const identityAvailable = meRecord?.identityVerificationAvailable === true;
  // Last session status — surfaces "attempted but not approved" states.
  const identityStatus = (meRecord?.identity_verification_status as string | null | undefined) ?? null;
  const identityDeclined = !identityVerified && ['Declined', 'Expired', 'Kyc Expired', 'Abandoned'].includes(identityStatus ?? '');
  const identityInReview = !identityVerified && ['In Review', 'Resubmitted'].includes(identityStatus ?? '');
  // Member has just come back from Didit's hosted flow. The flip is async (via
  // webhook), so on return we show a "confirming" state and poll /api/me a few
  // times — we NEVER claim "Verified" until the real flag flips server-side.
  const justReturnedFromIdentity = searchParams.get('identity') === 'complete';

  useEffect(() => {
    if (!justReturnedFromIdentity || identityVerified) return;
    let tries = 0;
    const iv = setInterval(() => {
      tries += 1;
      void queryClient.invalidateQueries({ queryKey: ['me'] });
      if (tries >= 5) {
        clearInterval(iv);
        setIdentityPollDone(true);
      }
    }, 3000);
    return () => clearInterval(iv);
  }, [justReturnedFromIdentity, identityVerified, queryClient]);

  // Build attribute map to check verification status
  const attrMap = useMemo(() => {
    if (!attributes) return {} as Record<string, string>;
    return Object.fromEntries(attributes.map((a) => [a.key, a.value]));
  }, [attributes]);

  // Determine completion status from stored scores
  const verifications = VERIFICATION_DEFS.map((v) => {
    let completed = v.alwaysComplete;
    if (v.id === 'email') {
      completed = (me as Record<string, unknown>)?.email_verified === true;
    } else if (v.id === 'phone') {
      completed = (me as Record<string, unknown>)?.phone_verified === true;
    } else if (v.id === 'id') {
      completed = identityVerified;
    } else if (!completed && v.attrKey && attrMap[v.attrKey]) {
      const score = parseInt(attrMap[v.attrKey], 10);
      completed = !isNaN(score) && score >= 60;
    }
    // ID Verification is Coming Soon ONLY while the feature flag is off — the
    // same server flag the session endpoint gates on, so the card can't drift.
    const comingSoon = v.id === 'id' ? !identityAvailable : v.comingSoon;
    return { ...v, completed, comingSoon };
  });

  // Use API-provided trust score and level (from /api/me)
  const trustScore = (me as Record<string, unknown>)?.trust_score as number ?? 10;
  const trustLevel = (me as Record<string, unknown>)?.trust_level as number ?? 1;
  const levelName = trustHistory?.levelName
    ?? (TRUST_LEVEL_NAMES[trustLevel] ?? 'Basic');

  async function handleAddEmail() {
    if (!emailInput.includes('@')) return;
    setEmailSending(true);
    try {
      await addEmail(emailInput);
      setEmailSent(true);
    } catch { /* ignore */ }
    setEmailSending(false);
  }

  function handleVerificationSuccess() {
    void queryClient.invalidateQueries({ queryKey: ['profile', 'attributes'] });
    void queryClient.invalidateQueries({ queryKey: ['me'] });
  }

  async function handleStartIdentity() {
    setIdentityError(false);
    setIdentityStarting(true);
    try {
      const result = await createIdentitySession();
      if (result.url) {
        // Enter Didit's hosted flow (full-page redirect). Didit returns the
        // member to /trust-center?identity=complete when done.
        window.location.href = result.url;
        return; // navigating away — leave the spinner up
      }
      if (result.already_verified) {
        void queryClient.invalidateQueries({ queryKey: ['me'] });
      }
      setIdentityStarting(false);
    } catch {
      setIdentityError(true);
      setIdentityStarting(false);
    }
  }

  return (
    <NavShell>
      <motion.main initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }} className="px-4 sm:px-6 py-6 space-y-8">
        {/* Hero Banner */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#1e1b4b] to-[#312e81] p-6 sm:p-8 text-white">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/3" />
          <div className="relative">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center">
                <Shield className="w-7 h-7 text-purple-300" />
              </div>
              <div>
                <h1 className="text-2xl font-bold font-display">Trust Center · {levelName} <span className="text-purple-300 text-lg font-normal">(L{trustLevel})</span></h1>
                <p className="text-purple-300 text-sm mt-0.5">Complete verifications to unlock higher paying surveys and faster cashouts</p>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-purple-200">Trust Score</span>
                <span className="font-bold">{trustScore}/100</span>
              </div>
              <div className="w-full bg-white/10 rounded-full h-3">
                <div className="bg-[#5B4FE8] h-3 rounded-full transition-all duration-500" style={{ width: `${trustScore}%` }} />
              </div>
            </div>
          </div>
        </div>

        {/* Two-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: Verifications */}
          <div>
            <h2 className="text-lg font-bold font-display text-[#111827] mb-2">Available Verifications</h2>
            <p className="text-xs text-[#6B7280] mb-2">
              Your trust score reflects your survey quality history. Complete verifications and surveys to increase it.
              Verifying your ID is the step that unlocks the top-tier money benefits — instant points and no-review cashouts.
            </p>
            <p className="text-xs text-[#5B4FE8] font-medium mb-4">
              {`+${TRUST_RECOVERY_DELTAS.CLEAN_SURVEY} per clean survey completion (up to +${TRUST_RECOVERY_DELTAS.CLEAN_SURVEY_DAILY_CAP}/day) · +${TRUST_RECOVERY_DELTAS.SURVEY_MILESTONE_10} at 10 completions · +${TRUST_RECOVERY_DELTAS.STREAK_7} on 7-day streaks`}
            </p>
            <div className="space-y-3">
              {verifications.map((v) => {
                const Icon = v.icon;
                return (
                  <div key={v.id} id={v.id === 'id' ? 'id-verification-card' : undefined} className="bg-white rounded-2xl shadow-sm p-5 flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${v.completed ? 'bg-green-50' : 'bg-[#F8F9FA]'}`}>
                      {v.completed ? <CheckCircle2 className="w-5 h-5 text-[#10B981]" /> : <Icon className="w-5 h-5 text-[#6B7280]" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <h3 className="font-bold text-[#111827] text-sm">{v.name}</h3>
                        <span className="inline-flex items-center gap-1 text-xs font-medium bg-[#EEF2FF] text-[#5B4FE8] px-3 py-1 rounded-full">
                          <Shield className="w-3 h-3" /> +{v.trustScore} trust score
                        </span>
                      </div>
                      <p className="text-sm text-[#6B7280] mt-1">{v.description}</p>
                      {v.rewardPoints > 0 && (
                        <p className="text-xs text-[#10B981] font-medium mt-1">Also awards {v.rewardPoints} reward points</p>
                      )}
                      {!v.completed && v.id !== 'id' && (
                        <button
                          onClick={
                            v.id === 'email' ? () => setShowEmailForm(true) :
                            v.id === 'writing' ? () => setWritingModalOpen(true) :
                            v.id === 'video' ? () => setVideoModalOpen(true) :
                            v.id === 'phone' ? () => setPhoneModalOpen(true) :
                            undefined
                          }
                          disabled={v.comingSoon}
                          className={`mt-3 px-5 py-2 rounded-full text-xs font-medium transition-colors ${
                            v.comingSoon ? 'bg-gray-100 text-[#6B7280] cursor-not-allowed' : 'bg-[#5B4FE8] text-white hover:bg-[#4338CA]'
                          }`}
                        >
                          {v.comingSoon ? 'Coming Soon' : 'Verify'}
                        </button>
                      )}
                      {/* ID Verification — live action / processing / verified states */}
                      {v.id === 'id' && !v.completed && (
                        v.comingSoon ? (
                          <button
                            disabled
                            className="mt-3 px-5 py-2 rounded-full text-xs font-medium bg-gray-100 text-[#6B7280] cursor-not-allowed"
                          >
                            Coming Soon
                          </button>
                        ) : identityInReview ? (
                          <div className="mt-3 flex items-center gap-2 text-xs text-[#5B4FE8] font-medium">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span>Verification in review — we'll update this once it's confirmed.</span>
                          </div>
                        ) : identityDeclined ? (
                          <div className="mt-3">
                            <p className="text-xs text-[#6B7280] mb-2">Verification didn&apos;t pass. You can try again.</p>
                            <button
                              onClick={() => void handleStartIdentity()}
                              disabled={identityStarting}
                              className="px-5 py-2 rounded-full text-xs font-medium transition-colors bg-[#5B4FE8] text-white hover:bg-[#4338CA] disabled:opacity-40"
                            >
                              {identityStarting ? 'Starting…' : 'Try again'}
                            </button>
                            {identityError && (
                              <p className="mt-2 text-xs text-red-500">Couldn&apos;t start verification. Please try again.</p>
                            )}
                          </div>
                        ) : justReturnedFromIdentity ? (
                          <div className="mt-3">
                            {!identityPollDone ? (
                              <div className="flex items-center gap-2 text-xs text-[#5B4FE8] font-medium">
                                <Loader2 className="w-4 h-4 animate-spin" />
                                <span>Verification submitted — we're confirming it now.</span>
                              </div>
                            ) : (
                              <p className="text-xs text-[#6B7280]">
                                Not confirmed yet. If you just finished, it can take a moment — check again shortly, or start over.
                              </p>
                            )}
                            <div className="mt-2 flex items-center gap-3">
                              <button
                                onClick={() => void queryClient.invalidateQueries({ queryKey: ['me'] })}
                                className="text-xs text-[#5B4FE8] font-medium underline"
                              >
                                Check status
                              </button>
                              {identityPollDone && (
                                <button
                                  onClick={() => void handleStartIdentity()}
                                  disabled={identityStarting}
                                  className="text-xs text-[#5B4FE8] font-medium underline disabled:opacity-40"
                                >
                                  Start over
                                </button>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div className="mt-3">
                            <button
                              onClick={() => void handleStartIdentity()}
                              disabled={identityStarting}
                              className="px-5 py-2 rounded-full text-xs font-medium transition-colors bg-[#5B4FE8] text-white hover:bg-[#4338CA] disabled:opacity-40"
                            >
                              {identityStarting ? 'Starting…' : 'Verify identity'}
                            </button>
                            {identityError && (
                              <p className="mt-2 text-xs text-red-500">Couldn't start verification. Please try again.</p>
                            )}
                          </div>
                        )
                      )}
                      {v.completed && v.id !== 'id' && (
                        <span className="inline-block mt-2 text-xs text-[#10B981] font-medium">Completed</span>
                      )}
                      {v.completed && v.id === 'id' && (
                        <span className="inline-flex items-center gap-1 mt-2 text-xs text-[#10B981] font-medium">
                          <CheckCircle2 className="w-3.5 h-3.5" /> Verified{identityVerifiedAt ? ` · ${formatShortDate(identityVerifiedAt)}` : ''}
                        </span>
                      )}
                      {v.id === 'email' && showEmailForm && !v.completed && (
                        emailSent ? (
                          <p className="mt-3 text-xs text-[#10B981] font-medium">Check your email for a verification link.</p>
                        ) : (
                          <div className="mt-3 flex gap-2">
                            <input
                              type="email"
                              value={emailInput}
                              onChange={(e) => setEmailInput(e.target.value)}
                              placeholder="you@example.com"
                              className="flex-1 border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#5B4FE8]"
                            />
                            <button
                              onClick={() => void handleAddEmail()}
                              disabled={!emailInput.includes('@') || emailSending}
                              className="bg-[#5B4FE8] text-white px-4 py-2 rounded-lg text-xs font-medium disabled:opacity-40"
                            >
                              {emailSending ? '...' : 'Send link'}
                            </button>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Score History */}
            <div className="mt-8">
              <div className="flex items-center gap-2 mb-3">
                <History className="w-5 h-5 text-[#5B4FE8]" />
                <h2 className="text-lg font-bold font-display text-[#111827]">Score History</h2>
              </div>
              {trustHistory && trustHistory.history.length > 0 ? (
                <div className="bg-white rounded-2xl shadow-sm p-5">
                  {trustHistory.history.slice(0, 10).map((entry, idx) => (
                    <ScoreHistoryRow key={`${entry.createdAt}-${idx}`} entry={entry} />
                  ))}
                </div>
              ) : (
                <div className="bg-white rounded-2xl shadow-sm p-5 text-sm text-[#6B7280]">
                  Your score history will appear here once you start earning trust events.
                </div>
              )}
            </div>
          </div>

          {/* Right: Level Benefits */}
          <div>
            <h2 className="text-lg font-bold font-display text-[#111827] mb-4">Level Benefits</h2>
            <div className="bg-white rounded-2xl shadow-sm p-5 space-y-4">
              {LEVEL_BENEFITS.map((b) => {
                const levelReached = b.level <= trustLevel;
                // Money benefits (L4 instant credit, L5 no-review redemption) are
                // TWO-FACTOR: they require the trust level AND a verified identity.
                // Never show them as unlocked by tier alone.
                const moneyGated = 'moneyGated' in b && b.moneyGated === true;
                const isUnlocked = levelReached && (!moneyGated || identityVerified);
                const isCurrent = b.level === trustLevel;
                // Identity is the missing factor when the tier is money-gated and
                // the member isn't verified — surface the actionable path.
                const idBlocks = moneyGated && !identityVerified;
                return (
                  <div key={b.level} className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${isUnlocked ? 'bg-[#10B981]/10' : 'bg-[#F8F9FA]'}`}>
                      {isUnlocked ? <CheckCircle2 className="w-4 h-4 text-[#10B981]" /> : <Lock className="w-4 h-4 text-[#6B7280]" />}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-[#6B7280]">Level {b.level} · {b.range}</span>
                        {isCurrent && <span className="text-[10px] font-medium bg-[#10B981]/10 text-[#10B981] px-2 py-0.5 rounded-full">Current</span>}
                      </div>
                      <h3 className={`text-sm font-bold ${isUnlocked ? 'text-[#111827]' : 'text-[#6B7280]'}`}>{b.name}</h3>
                      <p className="text-xs text-[#6B7280] mt-0.5">{b.description}</p>
                      {idBlocks && (
                        identityAvailable ? (
                          <button
                            onClick={() => document.getElementById('id-verification-card')?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
                            className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-[#5B4FE8] hover:underline"
                          >
                            <CreditCard className="w-3 h-3" /> Verify your ID to unlock
                          </button>
                        ) : (
                          <p className="mt-1 text-[11px] font-medium text-[#6B7280]">Also unlocks with ID verification (coming soon)</p>
                        )
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </motion.main>

      {writingModalOpen && (
        <WritingSampleModal onClose={() => {
          setWritingModalOpen(false);
          handleVerificationSuccess();
        }} />
      )}

      <VideoVerificationModal
        isOpen={videoModalOpen}
        onClose={() => setVideoModalOpen(false)}
        onSuccess={() => {
          setVideoModalOpen(false);
          handleVerificationSuccess();
        }}
      />

      {phoneModalOpen && (
        <PhoneVerifyModal
          onClose={() => setPhoneModalOpen(false)}
          onSuccess={handleVerificationSuccess}
        />
      )}
    </NavShell>
  );
}
